import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, makeStyles, mergeClasses, MessageBar, MessageBarActions, MessageBarBody, Option, Spinner, Text, Textarea, tokens } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useCommonStyles, useRubricPiiScan, useStores } from '../../../common/hooks';
import { AiScrubScope, isAiScrubBatchConflictError, isAiScrubBatchEmptyError } from '../../../models';
import { AI_SCRUB_OVERWRITE_WARNING } from '../../../common/scrubbing/ai-scrub-overwrite-warning';
import { AI_SCRUBBING_RECOMMENDED_RUBRIC_TEMPLATE } from './ai-scrubbing-rubric-template';
import { pickSeedRubric } from './rubric-seed-precedence';
import { ScrubCorrelationId } from '../../shared-components/ai-scrubbing/ScrubCorrelationId';

const useStyles = makeStyles({
  piiWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },
});

const seedRubric = (defaultRubric?: string): string =>
  pickSeedRubric(undefined, defaultRubric, AI_SCRUBBING_RECOMMENDED_RUBRIC_TEMPLATE);

interface AiScrubbingTriggerDialogProps {
  isOpen: boolean;
  awardId: number;
  programId?: number;
  countryId?: number;
  defaultScrubbingRubric?: string;
  scope?: AiScrubScope;
  onClose: (batchRunId?: string, isEvaluation?: boolean) => void;
}

const AiScrubbingTriggerDialogImpl: React.FC<AiScrubbingTriggerDialogProps> = (props) => {
  const { isOpen, awardId, programId, countryId, defaultScrubbingRubric, scope = 'awards', onClose } = props;

  const { instance } = useMsal();
  const { apiStore, countryAwardPageUiStore } = useStores();
  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const { scan, piiWarning, clear } = useRubricPiiScan();

  const [rubricPrompt, setRubricPrompt] = useState<string>(seedRubric(defaultScrubbingRubric));
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [conflictBatchRunId, setConflictBatchRunId] = useState<string | undefined>(undefined);
  const [showEmptyMessage, setShowEmptyMessage] = useState<boolean>(false);

  const wasOpenRef = useRef<boolean>(false);
  const hasUserEditedRef = useRef<boolean>(false);

  const isCountryScope = scope === 'country-awards';
  const authorizedCountries = countryAwardPageUiStore.scrubDialogAuthorizedCountries;
  const selectedCountryId = countryAwardPageUiStore.scrubDialogSelectedCountryId;
  const submittedCount = countryAwardPageUiStore.scrubDialogSubmittedCount;
  const selectedCountryDisplayName = countryAwardPageUiStore.scrubDialogSelectedCountryDisplayName;

  const loadCountForCountry = async (selected?: number): Promise<void> => {
    if (selected == null || programId == null) {
      countryAwardPageUiStore.setScrubDialogSubmittedCount(undefined);
      return;
    }
    const statistic = await apiStore.getCountryAwardStatistic(instance, programId, selected);
    if (countryAwardPageUiStore.scrubDialogSelectedCountryId === selected) {
      countryAwardPageUiStore.setScrubDialogSubmittedCount(statistic.numberOfSubmittedNominations);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (isOpen && !wasOpenRef.current) {
      setRubricPrompt(seedRubric(defaultScrubbingRubric));
      hasUserEditedRef.current = false;
      setIsSubmitting(false);
      setConflictBatchRunId(undefined);
      setShowEmptyMessage(false);
      clear();
      countryAwardPageUiStore.resetScrubDialogCountryState();
      if (isCountryScope && programId != null) {
        void (async () => {
          const countries = await apiStore.getScrubCountries(instance, programId);
          if (cancelled) return;
          countryAwardPageUiStore.setScrubDialogAuthorizedCountries(countries);
          countryAwardPageUiStore.resolveScrubDialogDefaultCountry(countryId);
          await loadCountForCountry(countryAwardPageUiStore.scrubDialogSelectedCountryId);
        })();
      }

      // Upgrade the seed to the last run's rubric so the reviewer resumes where they left off.
      // Best-effort and non-clobbering: skip if they have started typing, the dialog closed, or there is no run.
      const seedFromLastRun = async (): Promise<void> => {
        try {
          const latest = isCountryScope
            ? (countryId != null ? await apiStore.getLatestCountryAiScrubBatchRun(instance, awardId, countryId) : undefined)
            : await apiStore.getLatestAiScrubBatchRun(instance, awardId);
          if (cancelled || hasUserEditedRef.current || !latest) return;
          // If the reviewer switched to a different country while this request was in flight, do not
          // apply the open-edge country's rubric over the newly selected one. Read the live store
          // value (the render-time selectedCountryId const is captured stale in this closure).
          if (isCountryScope
              && countryAwardPageUiStore.scrubDialogSelectedCountryId != null
              && countryAwardPageUiStore.scrubDialogSelectedCountryId !== countryId) return;
          if (latest.rubricPromptText && latest.rubricPromptText.trim() !== '') {
            setRubricPrompt(pickSeedRubric(latest.rubricPromptText, defaultScrubbingRubric, AI_SCRUBBING_RECOMMENDED_RUBRIC_TEMPLATE));
          }
        } catch {
          // Best-effort; the synchronous default seed stands.
        }
      };
      void seedFromLastRun();
    }
    wasOpenRef.current = isOpen;
    return () => {
      cancelled = true;
    };
  }, [isOpen, defaultScrubbingRubric, clear, awardId, scope, countryId, apiStore, instance]);

  const isSubmitDisabled = rubricPrompt.trim() === '' || isSubmitting || (isCountryScope && selectedCountryId == null);

  const onSubmit = async (): Promise<void> => {
    setConflictBatchRunId(undefined);
    setShowEmptyMessage(false);
    setIsSubmitting(true);
    try {
      const response = isCountryScope
        ? await apiStore.startCountryAiScrubBatch(instance, awardId, rubricPrompt, selectedCountryId!)
        : await apiStore.startAiScrubBatch(instance, awardId, rubricPrompt);
      onClose(response.batchRunId, response.isEvaluation);
    } catch (error) {
      if (isAiScrubBatchConflictError(error)) {
        setConflictBatchRunId(error.batchRunId);
      } else if (isAiScrubBatchEmptyError(error)) {
        setShowEmptyMessage(true);
      }
      setIsSubmitting(false);
    }
  };

  const renderCountrySelector = (): React.JSX.Element | null => {
    if (!isCountryScope) return null;
    return (
      <Dropdown
        placeholder='Select a country'
        value={selectedCountryDisplayName}
        selectedOptions={selectedCountryId != null ? [String(selectedCountryId)] : []}
        style={{ width: '100%', marginTop: '0.5rem' }}
        onOptionSelect={(_, data) => {
          const next = data.optionValue ? +data.optionValue : undefined;
          countryAwardPageUiStore.setScrubDialogSelectedCountryId(next);
          void loadCountForCountry(next);
        }}
      >
        {authorizedCountries.map((country) => (
          <Option key={country.countryId} value={String(country.countryId)} text={country.displayName}>
            {country.displayName}
          </Option>
        ))}
      </Dropdown>
    );
  };

  const renderScopedCountPreview = (): React.JSX.Element | null => {
    if (!isCountryScope || selectedCountryId == null || submittedCount == null) return null;
    return (
      <Text block style={{ marginTop: '0.5rem' }}>
        {submittedCount} submitted nomination{submittedCount === 1 ? '' : 's'} will be reviewed for {selectedCountryDisplayName}.
      </Text>
    );
  };

  return (
    <Dialog
      open={isOpen}
    >
      <DialogSurface
        className={mergeClasses(commonStyles.modalSurface, 'ai-scrubbing-trigger-dialog')}
      >
        <DialogBody
          className={commonStyles.modalBody}
        >
          <DialogTitle
            action={
              <Button
                title='close'
                appearance='transparent'
                icon={<DismissRegular />}
                onClick={() => onClose()}
                disabled={isSubmitting}
              />
            }
          >
            AI Review
          </DialogTitle>
          <DialogContent>
            <Text
              as='p'
            >
              {isCountryScope
                ? "Edit the rubric prompt below, then submit to start the AI review for the selected country. The batch runs against that country's submitted nominations and may take several minutes to complete."
                : 'Edit the rubric prompt below, then submit to start the AI scrubbing batch for this award. The batch runs against all submitted nominations and may take several minutes to complete.'}
            </Text>
            <Text as='p' weight='semibold' style={{ marginTop: '0.5rem' }}>
              {AI_SCRUB_OVERWRITE_WARNING}
            </Text>
            {renderCountrySelector()}
            {renderScopedCountPreview()}
            <Textarea
              value={rubricPrompt}
              onChange={(_, data) => {
                setRubricPrompt(data.value);
                clear();
                hasUserEditedRef.current = true;
              }}
              onBlur={(event) => scan(event.currentTarget.value)}
              resize='vertical'
              rows={8}
              style={{ width: '100%', marginTop: '0.5rem' }}
            />
            {piiWarning && <Text block className={styles.piiWarning}>{piiWarning}</Text>}
            {conflictBatchRunId && (
              <MessageBar intent='warning' style={{ marginTop: '0.5rem' }}>
                <MessageBarBody>A scrub batch is already running for {isCountryScope ? 'this country' : 'this award'}. <ScrubCorrelationId correlationId={conflictBatchRunId} /></MessageBarBody>
                <MessageBarActions>
                  <Button appearance='transparent' onClick={() => onClose(conflictBatchRunId)}>View it</Button>
                </MessageBarActions>
              </MessageBar>
            )}
            {showEmptyMessage && (
              <MessageBar intent='info' style={{ marginTop: '0.5rem' }}>
                <MessageBarBody>There are no eligible nominations to scrub for {isCountryScope ? 'this country' : 'this award'}.</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions
            className='buttons'
          >
            <Button
              appearance='secondary'
              onClick={() => onClose()}
              disabled={isSubmitting}
              autoFocus
            >
              Cancel
            </Button>
            <Button
              appearance='primary'
              onClick={onSubmit}
              icon={isSubmitting ? <Spinner size='extra-tiny' /> : null}
              disabled={isSubmitDisabled}
            >
              Start Batch
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const AiScrubbingTriggerDialog = observer(AiScrubbingTriggerDialogImpl);
